import React, { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Loader2, Plus, Edit, Trash2, X, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { FormField } from './FormField';

const PRICING_MODES = [
  { value: 'flat', label: 'Prezzo fisso' }, { value: 'per_person', label: 'Per persona' },
  { value: 'per_day', label: 'Per giorno' }, { value: 'per_person_per_day', label: 'Per persona × giorno' },
];
const SVC_TYPES = [
  { value: 'fixed', label: 'Fisso' }, { value: 'variable', label: 'Variabile' },
  { value: 'meal_plan', label: 'Piano pasti (con chef)' },
  { value: 'rental', label: 'Noleggio / NCC' },
  { value: 'on_request', label: 'Su richiesta' },
];
const FIELD_TYPES = [
  { value: 'text', label: 'Testo breve' },
  { value: 'textarea', label: 'Testo lungo' },
];

function newField() {
  return { id: crypto.randomUUID(), label_it: '', label_en: '', field_type: 'text', required: false, placeholder_it: '' };
}
function newVariant() {
  return { id: crypto.randomUUID(), name_it: '', name_en: '', price: 0, sort_order: 0, custom_fields: [] };
}

export default function Services() {
  const [properties, setProperties] = useState([]);
  const [selectedPropId, setSelectedPropId] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/properties'),
      api.get('/suppliers'),
    ]).then(([propRes, supRes]) => {
      setProperties(propRes.data);
      if (propRes.data.length > 0) setSelectedPropId(propRes.data[0].id);
      setSuppliers((supRes.data || []).filter(s => s.is_active !== false));
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!selectedPropId) return;
    setLoading(true);
    try {
      const res = await api.get(`/services?property_id=${selectedPropId}`);
      setServices(res.data);
    } catch { } finally { setLoading(false); }
  }, [selectedPropId]);

  useEffect(() => { load(); }, [load]);

  const EMPTY = {
    property_id: selectedPropId, slug: '', name_it: '', name_en: '',
    description_it: '', description_en: '', service_type: 'fixed',
    pricing_mode: 'flat', price: 0, category: '', supplier_email: '',
    markup_percentage: 0,
    meal_breakfast_price: 0, meal_lunch_price: 0, meal_dinner_price: 0,
    variants: [],
    is_active: true, sort_order: 0,
  };

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY, property_id: selectedPropId }); setPanelOpen(true); };
  const openEdit = (s) => { setEditing(s.id); setForm({ ...EMPTY, ...s }); setPanelOpen(true); };

  const save = async () => {
    if (!form.name_it || !form.slug) { toast.error('Nome e slug obbligatori'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/services/${editing}`, form);
        toast.success('Servizio aggiornato');
      } else {
        await api.post('/services', form);
        toast.success('Servizio creato');
      }
      setPanelOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm('Eliminare?')) return;
    await api.delete(`/services/${id}`);
    load();
    toast.success('Servizio eliminato');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="label-gold">Gestione</p>
          <h2 className="font-serif text-3xl text-white font-light mt-1">Servizi</h2>
        </div>
        <button onClick={openCreate} className="btn-gold flex items-center gap-2" data-testid="new-service-btn">
          <Plus size={14} /> Nuovo Servizio
        </button>
      </div>
      <select className="input-dark max-w-xs" value={selectedPropId} onChange={e => setSelectedPropId(e.target.value)}>
        {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      {loading ? <div className="flex justify-center py-12"><Loader2 size={28} className="animate-spin text-[#c9a84c]" /></div> : (
        <div className="card-dark rounded-lg overflow-hidden">
          {services.length === 0 ? <p className="text-[var(--lux-text-muted-3)] text-sm p-6">Nessun servizio.</p> : (
            <table className="w-full table-dark">
              <thead><tr>
                <th className="text-left">Nome</th>
                <th className="text-left hidden sm:table-cell">Tipo</th>
                <th className="text-left hidden md:table-cell">Prezzo</th>
                <th className="text-left hidden md:table-cell">Modo</th>
                <th className="text-left">Attivo</th>
                <th />
              </tr></thead>
              <tbody>
                {services.map(s => (
                  <tr key={s.id}>
                    <td>
                      <p className="text-white">{s.name_it}</p>
                      {s.category && <p className="text-[var(--lux-text-muted-3)] text-xs">{s.category}</p>}
                    </td>
                    <td className="hidden sm:table-cell text-[var(--lux-text-muted-2)] text-xs">{s.service_type}</td>
                    <td className="hidden md:table-cell">
                      {s.supplier_email ? (
                        <span className="text-xs text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded">Su richiesta</span>
                      ) : (
                        <span className="text-[#c9a84c]">€{s.price}</span>
                      )}
                    </td>
                    <td className="hidden md:table-cell text-[var(--lux-text-muted-2)] text-xs">{s.pricing_mode}</td>
                    <td>
                      <span className={`w-2 h-2 rounded-full inline-block ${s.is_active ? 'bg-green-500' : 'bg-[var(--lux-border-3)]'}`} />
                    </td>
                    <td>
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => openEdit(s)} className="p-1.5 text-[var(--lux-text-muted-3)] hover:text-white"><Edit size={13} /></button>
                        <button onClick={() => remove(s.id)} className="p-1.5 text-[var(--lux-text-muted-3)] hover:text-red-400"><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <SheetContent className="bg-[var(--lux-surface-1)] border-l border-[#222] text-white w-full sm:max-w-md overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle className="font-serif text-2xl font-light text-white">{editing ? 'Modifica' : 'Nuovo'} Servizio</SheetTitle>
          </SheetHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Nome IT *" name="name_it" form={form} setForm={setForm} />
              <FormField label="Nome EN" name="name_en" form={form} setForm={setForm} />
            </div>
            <FormField label="Slug *" name="slug" form={form} setForm={setForm} />
            <FormField label="Descrizione IT" name="description_it" rows={2} form={form} setForm={setForm} />
            <FormField label="Categoria" name="category" form={form} setForm={setForm} />
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Tipo" name="service_type" options={SVC_TYPES} form={form} setForm={setForm} />
              <FormField label="Calcolo prezzo" name="pricing_mode" options={PRICING_MODES} form={form} setForm={setForm} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Prezzo €" name="price" type="number" form={form} setForm={setForm} />
              <FormField label="Ordine" name="sort_order" type="number" form={form} setForm={setForm} />
            </div>

            {/* Meal plan prices — shown only when service_type === meal_plan */}
            {form.service_type === 'meal_plan' && (
              <div className="border border-[#c9a84c]/20 rounded-lg p-4 space-y-3 bg-[var(--lux-surface-1)]">
                <p className="text-[#c9a84c] text-xs uppercase tracking-widest">Prezzi Piano Pasti (€/persona/giorno)</p>
                <p className="text-[var(--lux-text-muted-3)] text-xs">Chef incluso. Lascia 0 se il prezzo è su richiesta tramite fornitore.</p>
                <div className="grid grid-cols-3 gap-3">
                  <FormField label="Colazione" name="meal_breakfast_price" type="number" form={form} setForm={setForm} testId="service-meal-breakfast" />
                  <FormField label="Pranzo" name="meal_lunch_price" type="number" form={form} setForm={setForm} testId="service-meal-lunch" />
                  <FormField label="Cena" name="meal_dinner_price" type="number" form={form} setForm={setForm} testId="service-meal-dinner" />
                </div>
              </div>
            )}

            {/* Rental variants — shown only when service_type === rental */}
            {form.service_type === 'rental' && (
              <div className="border border-[#c9a84c]/20 rounded-lg p-4 space-y-4 bg-[var(--lux-surface-1)]">
                <div className="flex items-center justify-between">
                  <p className="text-[#c9a84c] text-xs uppercase tracking-widest">Varianti Noleggio</p>
                  <button type="button" onClick={() => setForm(p => ({ ...p, variants: [...(p.variants || []), newVariant()] }))}
                    className="btn-ghost text-xs flex items-center gap-1 py-1 px-2">
                    <Plus size={12} /> Aggiungi variante
                  </button>
                </div>
                {(!form.variants || form.variants.length === 0) && (
                  <p className="text-[var(--lux-border-4)] text-xs">Nessuna variante. Aggiungi almeno una (es. "Recupero aeroporto", "Struttura → aeroporto").</p>
                )}
                {(form.variants || []).map((variant, vi) => (
                  <div key={variant.id} className="border border-[var(--lux-border-2)] rounded-lg p-3 space-y-3 bg-[var(--lux-bg)]">
                    <div className="flex items-center justify-between">
                      <p className="text-white text-xs font-medium">Variante {vi + 1}</p>
                      <button type="button" onClick={() => setForm(p => ({ ...p, variants: p.variants.filter((_, i) => i !== vi) }))}
                        className="text-[var(--lux-border-4)] hover:text-red-400 transition-colors"><X size={12} /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="label-muted block mb-1 text-xs">Nome IT *</label>
                        <input className="input-dark" value={variant.name_it || ''}
                          onChange={e => setForm(p => ({ ...p, variants: p.variants.map((v, i) => i === vi ? { ...v, name_it: e.target.value } : v) }))}
                          placeholder="Recupero in aeroporto" />
                      </div>
                      <div>
                        <label className="label-muted block mb-1 text-xs">Nome EN</label>
                        <input className="input-dark" value={variant.name_en || ''}
                          onChange={e => setForm(p => ({ ...p, variants: p.variants.map((v, i) => i === vi ? { ...v, name_en: e.target.value } : v) }))} />
                      </div>
                    </div>
                    {/* Custom fields for this variant */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-[var(--lux-text-muted-3)] text-xs">Campi compilabili dal cliente</p>
                        <button type="button"
                          onClick={() => setForm(p => ({ ...p, variants: p.variants.map((v, i) => i === vi ? { ...v, custom_fields: [...(v.custom_fields || []), newField()] } : v) }))}
                          className="text-[#c9a84c] text-xs hover:underline flex items-center gap-0.5">
                          <Plus size={10} /> Campo
                        </button>
                      </div>
                      {(variant.custom_fields || []).map((field, fi) => (
                        <div key={field.id} className="flex gap-2 items-end p-2 bg-[var(--lux-bg)] rounded border border-[var(--lux-surface-3)]">
                          <div className="flex-1">
                            <label className="label-muted block mb-1 text-[10px]">Etichetta IT *</label>
                            <input className="input-dark text-xs" value={field.label_it || ''} placeholder="Es. Aeroporto di partenza"
                              onChange={e => setForm(p => ({ ...p, variants: p.variants.map((v, i) => i === vi ? {
                                ...v, custom_fields: v.custom_fields.map((f, j) => j === fi ? { ...f, label_it: e.target.value } : f)
                              } : v) }))} />
                          </div>
                          <div className="w-28">
                            <label className="label-muted block mb-1 text-[10px]">Tipo</label>
                            <select className="input-dark text-xs" value={field.field_type || 'text'}
                              onChange={e => setForm(p => ({ ...p, variants: p.variants.map((v, i) => i === vi ? {
                                ...v, custom_fields: v.custom_fields.map((f, j) => j === fi ? { ...f, field_type: e.target.value } : f)
                              } : v) }))}>
                              {FIELD_TYPES.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                            </select>
                          </div>
                          <label className="flex items-center gap-1 text-[10px] text-[var(--lux-text-muted-2)] cursor-pointer whitespace-nowrap mb-2">
                            <input type="checkbox" checked={field.required || false}
                              onChange={e => setForm(p => ({ ...p, variants: p.variants.map((v, i) => i === vi ? {
                                ...v, custom_fields: v.custom_fields.map((f, j) => j === fi ? { ...f, required: e.target.checked } : f)
                              } : v) }))} className="accent-[#c9a84c]" />
                            Obblig.
                          </label>
                          <button type="button"
                            onClick={() => setForm(p => ({ ...p, variants: p.variants.map((v, i) => i === vi ? {
                              ...v, custom_fields: v.custom_fields.filter((_, j) => j !== fi)
                            } : v) }))}
                            className="text-[var(--lux-border-3)] hover:text-red-400 mb-2"><X size={11} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div>
              {/* Supplier selector — dropdown if suppliers exist, text input as fallback */}
              <label className="label-muted block mb-1.5 text-xs">Fornitore (opzionale — rende il servizio "su richiesta")</label>
              {suppliers.length > 0 ? (
                <select
                  className="input-dark w-full"
                  value={form.supplier_email || ''}
                  onChange={e => setForm(p => ({ ...p, supplier_email: e.target.value }))}
                  data-testid="service-supplier-select"
                >
                  <option value="">— Nessun fornitore (prezzo fisso) —</option>
                  {suppliers.map(s => (
                    <option key={s.id} value={s.email}>{s.name} · {s.email}</option>
                  ))}
                </select>
              ) : (
                <FormField label="Email Fornitore" name="supplier_email" type="email" form={form} setForm={setForm} testId="service-supplier-email"
                  placeholder="fornitore@email.com" />
              )}
              {form.supplier_email && (
                <>
                  <p className="text-amber-400 text-xs mt-1.5 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
                    Il fornitore riceverà un'email automatica con link al portale per ogni richiesta. Il prezzo verrà mostrato come "Su richiesta".
                  </p>
                  <div className="mt-3 border border-[#c9a84c]/20 rounded-lg p-3 bg-[var(--lux-surface-1)]">
                    <p className="text-[#c9a84c] text-xs uppercase tracking-widest mb-2">Maggiorazione sul preventivo</p>
                    <div className="flex-1">
                      <FormField
                        label="Maggiorazione % (es. 50 = +50% sul costo fornitore)"
                        name="markup_percentage"
                        type="number"
                        form={form}
                        setForm={setForm}
                        testId="service-markup-percentage"
                      />
                    </div>
                    {(form.markup_percentage > 0) && (
                      <p className="text-[var(--lux-text-muted)] text-xs mt-1.5">
                        Esempio: fornitore quota €100 → prezzo ospite <span className="text-[#c9a84c] font-semibold">€{(100 * (1 + (form.markup_percentage || 0) / 100)).toFixed(2)}</span>
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>

            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.is_active ?? true} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} className="accent-[#c9a84c]" />
              <span className="text-sm text-[var(--lux-text-sec)]">Attivo</span>
            </label>
            <div className="flex gap-3 pt-4 border-t border-[#222]">
              <button onClick={save} disabled={saving} className="btn-gold flex-1 flex items-center justify-center gap-2">
                {saving && <Loader2 size={13} className="animate-spin" />}
                Salva
              </button>
              <button onClick={() => setPanelOpen(false)} className="btn-outline-gold flex-1">Annulla</button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
